import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Download, Save, Share2, Play, FolderOpen, Trash2, Loader2, Upload, Copy,
  CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = { credits: "Créditos IA", posts: "Publicaciones", campaigns: "Campañas Email", creative: "Piezas Creativas" };

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function toDatetimeLocal(d) {
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

async function downloadBlob(response, fallback) {
  const blob = await response.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const cd = response.headers?.["content-disposition"] || "";
  const match = cd.match(/filename="([^"]+)"/);
  a.download = match ? match[1] : fallback;
  a.href = url;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function Reportes() {
  const { user, isMaster } = useAuth();
  const [tab, setTab] = useState("builder");

  // Referencias globales
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);

  // Builder state
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const [name, setName] = useState("Reporte mensual");
  const [start, setStart] = useState(toDatetimeLocal(monthStart));
  const [end, setEnd] = useState(toDatetimeLocal(now));
  const [companyIds, setCompanyIds] = useState([]); // [] = todas
  const [userIds, setUserIds] = useState([]);
  const [include, setInclude] = useState({ credits: true, posts: true, campaigns: true, creative: true });
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Saved
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);

  // Share
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cs, us] = await Promise.all([
          api.get("/companies/mine"),
          isMaster ? api.get("/team/users") : Promise.resolve({ data: [] }),
        ]);
        setCompanies(cs.data);
        setUsers(us.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar catálogos");
      }
    })();
    loadSaved();
    // Bootstrap único al montar: catálogos + reportes guardados.
    // Intencional: NO reejecutar si cambian `isMaster` / `loadSaved` para
    // evitar loops de refetch al hidratar el AuthContext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSaved = async () => {
    setSavedLoading(true);
    try {
      const { data } = await api.get("/reports/");
      setSaved(data);
    } catch (e) {
      // silencio - user puede no tener acceso
    } finally { setSavedLoading(false); }
  };

  const buildFilters = () => ({
    name,
    start: toIso(start),
    end: toIso(end),
    company_ids: companyIds,
    user_ids: userIds,
    include,
  });

  const generatePreview = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/preview", buildFilters());
      setPreview(data);
      toast.success("Vista previa generada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setGenerating(false); }
  };

  const saveReport = async () => {
    try {
      await api.post("/reports/save", buildFilters());
      toast.success("Reporte guardado");
      await loadSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const exportFormat = async (fmt) => {
    try {
      const resp = await api.post(`/reports/export/${fmt}`, buildFilters(), { responseType: "blob" });
      await downloadBlob(resp, `reporte.${fmt}`);
      toast.success(`Exportado en ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  const openSaved = async (id) => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      // Rehidratar filtros
      const f = data.filters || {};
      setName(f.name || data.name || "Reporte");
      if (f.start) setStart(toDatetimeLocal(new Date(f.start)));
      if (f.end) setEnd(toDatetimeLocal(new Date(f.end)));
      setCompanyIds(f.company_ids || []);
      setUserIds(f.user_ids || []);
      setInclude(f.include || { credits: true, posts: true, campaigns: true, creative: true });
      setPreview(data.snapshot);
      setTab("builder");
      toast.success("Reporte cargado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("¿Eliminar reporte guardado?")) return;
    try {
      await api.delete(`/reports/${id}`);
      toast.success("Eliminado");
      await loadSaved();
    } catch (e) { toast.error("Error"); }
  };

  const toggleCompanyId = (id) =>
    setCompanyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleUserId = (id) =>
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Vista simple: parseamos y mostramos primeras 20 filas
      const lines = text.split(/\r?\n/).slice(0, 30);
      const rows = lines.map((l) => l.split(","));
      setPreview({
        name: `Importado: ${file.name}`,
        totals: {},
        credits: [], posts: [], campaigns: [], creative: [],
        __imported: rows,
        range_label: "Datos importados desde CSV",
        generated_at: new Date().toISOString(),
      });
      setTab("builder");
      toast.success("CSV importado");
    } catch { toast.error("Error al leer CSV"); }
    e.target.value = "";
  };

  const shareText = useMemo(() => {
    if (!preview) return "";
    const t = preview.totals || {};
    return `Reporte FStudy Manager · ${preview.name}\n` +
      `Rango: ${preview.range_label || ""}\n` +
      `Empresas: ${(preview.company_names || []).join(", ") || "Todas"}\n\n` +
      `📊 Créditos IA: ${t.credits_used || 0}\n` +
      `📱 Posts publicados: ${t.posts_published || 0}\n` +
      `📧 Campañas enviadas: ${t.campaigns_sent || 0}\n` +
      `🎨 Piezas creativas: ${t.creative_generated || 0}`;
  }, [preview]);

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="reportes-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <FileText className="h-8 w-8 text-brand-emerald" />
          Reportes
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-3xl">
          Construya reportes filtrables (empresas, usuarios, rango, tipos de datos). Vista previa,
          exportación en CSV/XLSX/PDF, guardado y compartir por WhatsApp.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reportes-tabs">
          <TabsTrigger value="builder" data-testid="tab-builder">Constructor</TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved">Reportes guardados</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-6 mt-6">
          <div className="card-elevated p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Título</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="report-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Desde</Label>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Hasta</Label>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-2 block">
                Empresas ({companyIds.length === 0 ? "todas visibles" : `${companyIds.length} seleccionadas`})
              </Label>
              <div className="flex flex-wrap gap-2">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => toggleCompanyId(c.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${companyIds.includes(c.id) ? "border-brand-gold bg-brand-gold-soft text-brand-gold-dark shadow-gold" : "border-border bg-white text-brand-ink hover:bg-stone-50"}`}
                    data-testid={`filter-company-${c.id}`}
                  >
                    <span className="h-3 w-3 rounded" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {isMaster && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-2 block">
                  Usuarios ({userIds.length === 0 ? "todos" : `${userIds.length} seleccionados`})
                </Label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => toggleUserId(u.id)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition ${userIds.includes(u.id) ? "border-brand-emerald bg-brand-emerald-soft text-brand-emerald-dark" : "border-border bg-white text-brand-ink hover:bg-stone-50"}`}
                      data-testid={`filter-user-${u.id}`}
                    >
                      {u.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold mb-2 block">
                Datos a incluir
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(KIND_LABELS).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between rounded-md border border-border bg-white px-3 py-2">
                    <span className="text-sm">{v}</span>
                    <Switch checked={!!include[k]} onCheckedChange={() => setInclude({ ...include, [k]: !include[k] })} data-testid={`include-${k}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={generatePreview} disabled={generating} data-testid="generate-preview-button"
                className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generando…</> : <><Play className="h-4 w-4 mr-2" />Vista previa</>}
              </Button>
              <Button onClick={saveReport} variant="outline" data-testid="save-report-button">
                <Save className="h-4 w-4 mr-2" /> Guardar
              </Button>
              <Button onClick={() => exportFormat("csv")} variant="outline" data-testid="export-csv-button">
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button onClick={() => exportFormat("xlsx")} variant="outline" data-testid="export-xlsx-button">
                <Download className="h-4 w-4 mr-2" /> XLSX
              </Button>
              <Button onClick={() => exportFormat("pdf")}
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
                data-testid="export-pdf-button">
                <Download className="h-4 w-4 mr-2" /> PDF
              </Button>
              {preview && (
                <Button onClick={() => setShareOpen(true)} variant="outline" data-testid="share-button">
                  <Share2 className="h-4 w-4 mr-2" /> Compartir
                </Button>
              )}
              <label className="inline-flex items-center gap-2 border border-border rounded-md px-3 py-1.5 text-sm cursor-pointer hover:bg-stone-50" data-testid="import-csv-label">
                <Upload className="h-4 w-4" /> Importar CSV
                <input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="import-csv-input" />
              </label>
            </div>
          </div>

          {preview && <PreviewSection data={preview} />}
        </TabsContent>

        <TabsContent value="saved" className="mt-6">
          <div className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/60">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Autor</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedLoading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-brand-emerald" /></TableCell></TableRow>
                ) : saved.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-stone-500">Aún no ha guardado reportes.</TableCell></TableRow>
                ) : (
                  saved.map((r) => (
                    <TableRow key={r.id} data-testid={`saved-row-${r.id}`}>
                      <TableCell className="font-medium text-brand-ink">{r.name}</TableCell>
                      <TableCell className="text-sm text-stone-500">{r.created_by_name}</TableCell>
                      <TableCell className="text-xs text-stone-500">{fmt(r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openSaved(r.id)} data-testid={`open-saved-${r.id}`}>
                            <FolderOpen className="h-4 w-4 mr-1" /> Abrir
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteSaved(r.id)}
                            className="text-destructive hover:bg-destructive/10" data-testid={`delete-saved-${r.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Compartir reporte</DialogTitle>
            <DialogDescription>Comparte el resumen por WhatsApp o cópialo al portapapeles.</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-stone-50 p-3 text-xs whitespace-pre-wrap font-mono max-h-56 overflow-y-auto">
            {shareText}
          </div>
          <DialogFooter>
            <Button variant="outline"
              onClick={() => { navigator.clipboard.writeText(shareText); toast.success("Copiado"); }}
              data-testid="copy-summary-button">
              <Copy className="h-4 w-4 mr-2" /> Copiar
            </Button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
              target="_blank" rel="noreferrer"
              data-testid="whatsapp-share-link"
            >
              <Button className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                <Share2 className="h-4 w-4 mr-2" /> Abrir WhatsApp
              </Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewSection({ data }) {
  const t = data.totals || {};
  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="preview-section">
      {/* Header con branding */}
      <div className="card-elevated p-6 bg-gradient-to-br from-brand-emerald-soft/60 to-brand-gold-soft/40 border-l-4 border-brand-emerald">
        <div className="flex items-center gap-3 mb-4">
          <img src="/assets/logo.png" alt="FStudy" className="h-12 w-12" />
          <div>
            <div className="font-serif-display text-3xl text-brand-ink font-semibold">{data.name}</div>
            <div className="text-xs text-stone-500 mt-0.5">
              {data.range_label} · Empresas: {(data.company_names || []).join(", ") || "Todas visibles"} ·
              Generado {fmt(data.generated_at)}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Créditos IA" value={t.credits_used || 0} />
          <StatCard label="Posts publicados" value={t.posts_published || 0} sub={`${t.posts_total || 0} totales`} />
          <StatCard label="Campañas enviadas" value={t.campaigns_sent || 0} sub={`${t.campaigns_total || 0} totales`} />
          <StatCard label="Piezas creativas" value={t.creative_generated || 0} sub={`${t.creative_approved || 0} aprobadas`} />
        </div>
      </div>

      {(data.credits || []).length > 0 && (
        <PreviewTable title="Créditos IA" data-testid="preview-credits"
          headers={["Fecha", "Empresa", "Usuario", "Prompt", "Créd."]}
          rows={data.credits.slice(0, 100).map((r) => [fmt(r.timestamp), r.company_name, r.user_name, r.prompt, r.credits_used])}
        />
      )}
      {(data.posts || []).length > 0 && (
        <PreviewTable title="Publicaciones" data-testid="preview-posts"
          headers={["Fecha", "Empresa", "Plataforma", "Autor", "Estado", "Contenido"]}
          rows={data.posts.slice(0, 100).map((r) => [fmt(r.at), r.company_name, r.platform, r.author_name, r.status, (r.content || "").slice(0, 100)])}
        />
      )}
      {(data.campaigns || []).length > 0 && (
        <PreviewTable title="Campañas Email" data-testid="preview-campaigns"
          headers={["Fecha", "Empresa", "Nombre", "Asunto", "Estado", "Env.", "Aper."]}
          rows={data.campaigns.slice(0, 100).map((r) => [fmt(r.at), r.company_name, r.name, r.subject, r.status, (r.stats || {}).sent || 0, (r.stats || {}).opened || 0])}
        />
      )}
      {(data.creative || []).length > 0 && (
        <PreviewTable title="Piezas Creativas" data-testid="preview-creative"
          headers={["Fecha", "Empresa", "Autor", "Estado", "Prompt"]}
          rows={data.creative.slice(0, 100).map((r) => [fmt(r.at), r.company_name, r.author_name, r.status, (r.prompt || "").slice(0, 100)])}
        />
      )}

      {data.__imported && (
        <div className="card-elevated p-4">
          <div className="font-serif-display text-xl text-brand-ink font-semibold mb-2">Datos importados (primeras 30 filas)</div>
          <div className="max-h-96 overflow-auto text-xs font-mono">
            <table className="w-full">
              <tbody>
                {data.__imported.map((row, i) => (
                  <tr key={i} className="border-b border-border">
                    {row.map((cell, j) => (<td key={j} className="p-1 pr-3">{cell}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg bg-white border border-border p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      <div className="text-3xl font-serif-display text-brand-emerald-dark font-bold mt-1">{value}</div>
      {sub && <div className="text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

function PreviewTable({ title, headers, rows, "data-testid": testid }) {
  return (
    <div className="card-elevated overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border bg-stone-50/60">
        <div className="font-serif-display text-xl text-brand-ink font-semibold">
          {title} <span className="text-xs text-stone-500 font-sans font-normal">({rows.length} filas mostradas)</span>
        </div>
      </div>
      <div className="max-h-96 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60">
              {headers.map((h) => <TableHead key={h}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {row.map((cell, j) => (
                  <TableCell key={j} className="text-xs text-stone-700 max-w-md truncate">{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
